// components/tracking/TrackingHistory.jsx - Compact version with scroll indicators
import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  FaListUl, FaMapMarkerAlt, FaCheck, FaTruckMoving, 
  FaArrowUp, FaClock,
  FaBoxOpen, FaUndo, FaTimesCircle,
  FaChevronDown, FaChevronUp
} from 'react-icons/fa';
import StatusBadge from './StatusBadge';

const TrackingHistory = ({ trackingHistory, formatDate }) => {
  const [showScrollDown, setShowScrollDown] = useState(false);
  const [showScrollUp, setShowScrollUp] = useState(false);
  const scrollContainerRef = useRef(null);

  // Check scroll position to show/hide scroll indicators
  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const isScrollable = scrollHeight > clientHeight;
    
    if (!isScrollable) {
      setShowScrollDown(false);
      setShowScrollUp(false);
      return;
    }

    // Show scroll down arrow if not at bottom
    setShowScrollDown(scrollTop < scrollHeight - clientHeight - 10);
    
    // Show scroll up arrow if not at top
    setShowScrollUp(scrollTop > 10);
  };

  // Check initial scroll state
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      handleScroll();
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [trackingHistory]);

  // Initial check when component mounts or data changes
  useEffect(() => {
    setTimeout(() => {
      handleScroll();
    }, 100);
  }, [trackingHistory]);

  const scrollToTop = () => {
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToBottom = () => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
    }
  };

  const getTimelineIcon = (item) => {
    const status = item.order_status_description.toLowerCase();
    const code = item.order_status_code || '';

    // Success states
    if (code === '100' || status.includes('delivered')) return <FaCheck className="text-white text-xs" />;
    if (code === '160' || status.includes('rto delivered')) return <FaUndo className="text-white text-xs" />;
    
    // In-progress states
    if (code === '70' || status.includes('in transit')) return <FaTruckMoving className="text-white text-xs" />;
    if (code === '230' || status.includes('rto in transit')) return <FaTruckMoving className="text-white text-xs" />;
    if (code === '80' || code === '150' || status.includes('out for delivery')) return <FaTruckMoving className="text-white text-xs" />;

    // Pickup states
    if (code === '60' || status.includes('picked up')) return <FaArrowUp className="text-white text-xs" />;
    if (code === '40' || status.includes('pickup pending')) return <FaClock className="text-white text-xs" />;
    if (code === '240' || status.includes('out for pickup')) return <FaBoxOpen className="text-white text-xs" />;

    // RTO states
    if (code === '130' || code === '140' || status.includes('rto') || status.includes('return')) return <FaUndo className="text-white text-xs" />;

    // Failed states
    if (code === '50' || code === '110' || code === '170' || status.includes('failed')) return <FaTimesCircle className="text-white text-xs" />;
    if (code === '120' || status.includes('cancelled')) return <FaTimesCircle className="text-white text-xs" />;

    // Default
    return <FaClock className="text-white text-xs" />;
  };

  const getTimelineDotColor = (item, isLatest) => {
    const status = item.order_status_description.toLowerCase();
    const code = item.order_status_code || '';

    if (isLatest) {
      return 'bg-blue-500 ring-4 ring-blue-100';
    }

    // Success states
    if (code === '100' || status.includes('delivered')) return 'bg-green-500';
    if (code === '160' || status.includes('rto delivered')) return 'bg-green-500';

    // In-progress states
    if (code === '70' || code === '230' || status.includes('in transit')) return 'bg-blue-500';
    if (code === '80' || code === '150' || status.includes('out for delivery')) return 'bg-purple-500';

    // Pickup states
    if (code === '60' || status.includes('picked up')) return 'bg-orange-500';
    if (code === '40' || code === '240' || status.includes('pickup')) return 'bg-yellow-500';

    // RTO states
    if (code === '130' || code === '140' || status.includes('rto')) return 'bg-orange-500';

    // Failed states
    if (status.includes('failed') || status.includes('cancelled')) return 'bg-red-500';

    // Default
    return 'bg-gray-400';
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-purple-600 rounded-xl">
            <FaListUl className="text-white text-lg" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Complete Tracking History</h2>
            <p className="text-sm text-gray-500">Detailed shipment journey</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Scroll hint for long history */}
          {trackingHistory.length > 4 && (
            <div className="bg-amber-50 border border-amber-200 px-3 py-1 rounded-full">
              <span className="text-xs text-amber-700 font-medium">📜 Scrollable</span>
            </div>
          )}
          <div className="bg-blue-50 px-3 py-1 rounded-full border border-blue-100">
            <span className="text-sm font-semibold text-blue-700">
              {trackingHistory.length} updates
            </span>
          </div>
        </div>
      </div>
      
      <div className="relative">
        {/* Timeline Line */}
        <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gray-200"></div>
        
        {/* Scroll Up Indicator */}
        {showScrollUp && (
          <div className="absolute top-0 left-0 right-0 z-20 bg-gradient-to-b from-white via-white to-transparent h-8 flex justify-center pt-1">
            <button
              onClick={scrollToTop}
              className="bg-blue-600 text-white p-1 rounded-full shadow-lg hover:bg-blue-700 transition-colors animate-bounce"
              title="Scroll to top"
            >
              <FaChevronUp className="text-xs" />
            </button>
          </div>
        )}
        
        <div 
          ref={scrollContainerRef}
          className="space-y-4 max-h-96 overflow-y-auto custom-scrollbar"
          onScroll={handleScroll}
        >
          {trackingHistory.map((item, idx) => {
            const isLatest = idx === 0;
            
            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.05, duration: 0.3 }}
                className="relative flex items-start gap-4"
              >
                {/* Timeline Dot */}
                <div className={`relative z-10 flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${getTimelineDotColor(item, isLatest)} transition-all duration-200 hover:scale-110`}>
                  {getTimelineIcon(item)}
                </div>

                {/* Content Card */}
                <div className={`flex-1 rounded-xl border p-4 transition-all duration-200 hover:shadow-md ${
                  isLatest 
                    ? 'bg-blue-50 border-blue-200' 
                    : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                }`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <StatusBadge 
                          status={item.order_status_description}
                          statusCode={item.order_status_code}
                          size="small"
                        />
                        {isLatest && (
                          <span className="px-2 py-1 text-xs font-bold text-blue-700 bg-blue-200 rounded-full">
                            LATEST
                          </span>
                        )}
                      </div>
                      
                      {/* Location */}
                      <div className="flex items-center gap-2 mb-2">
                        <FaMapMarkerAlt className="text-red-500 text-xs" />
                        <span className="text-sm font-medium text-gray-700">{item.order_location}</span>
                      </div>
                      
                      {/* Remarks */}
                      {item.order_remarks && (
                        <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 mt-3">
                          <p className="text-xs text-amber-600 font-medium mb-1 uppercase tracking-wide">
                            Additional Information
                          </p>
                          <p className="text-sm text-amber-900 italic">
                            "{item.order_remarks}"
                          </p>
                        </div>
                      )}
                    </div>
                    
                    {/* Timestamp */}
                    <div className="text-right ml-4 flex-shrink-0">
                      <div className="bg-white border border-gray-200 rounded-lg p-3 text-center min-w-20">
                        <div className="text-sm font-bold text-gray-900">
                          {new Date(item.order_status_date_time_ist).toLocaleDateString('en-IN', {
                            day: 'numeric',
                            month: 'short'
                          })}
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(item.order_status_date_time_ist).toLocaleTimeString('en-IN', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Scroll Down Indicator */}
        {showScrollDown && (
          <div className="absolute bottom-0 left-0 right-0 z-20 bg-gradient-to-t from-white via-white to-transparent h-8 flex justify-center pb-1">
            <button
              onClick={scrollToBottom}
              className="bg-blue-600 text-white p-1 rounded-full shadow-lg hover:bg-blue-700 transition-colors animate-bounce"
              title="Scroll to bottom"
            >
              <FaChevronDown className="text-xs" />
            </button>
          </div>
        )}

        {/* Empty State */}
        {trackingHistory.length === 0 && (
          <div className="text-center py-8">
            <FaClock className="mx-auto text-gray-400 text-2xl mb-3" />
            <p className="text-gray-600">No tracking history available</p>
            <p className="text-gray-500 text-sm mt-1">Check back later for updates</p>
          </div>
        )}
      </div>

      {/* Summary Footer */}
      {trackingHistory.length > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-100">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-4 text-gray-600">
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span>Latest</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>Delivered</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                <span>In Transit</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                <span>Failed</span>
              </div>
            </div>
            <div className="text-gray-500 text-xs">
              Started: {formatDate(trackingHistory[trackingHistory.length - 1]?.order_status_date_time_ist)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrackingHistory;